export type { FieldValidation } from "./contact-field-validators";
import type { FieldValidation } from "./contact-field-validators";
import {
  validateEmail,
  validatePhone,
  validateStatus,
  validateTags,
  validateCustomFields,
  validateEmailAliases,
  validatePhoneAliases,
  validateSocialLinks,
  validateBoolean,
  validateString,
  validateStringArray,
  validateDateString,
  validateUUID,
} from "./contact-field-validators";

const READ_ONLY_FIELDS = new Set([
  "id",
  "created_at",
  "created_by",
  "updated_at",
  "updated_by",
  "search_vector",
]);

const ALLOWED_CONTACT_FIELDS = new Set([
  "first_name",
  "last_name",
  "name",
  "email",
  "phone",
  "company_id",
  "company_name",
  "status",
  "tags",
  "notes",
  "custom_fields",
  "address",
  "city",
  "state",
  "country",
  "zip_code",
  "title",
  "source",
  "lifecycle_stage",
  "last_contacted_at",
  "last_enriched_at",
  "last_follow_up_task_created_at",
  "avatar_url",
  "email_aliases",
  "phone_aliases",
  "social_links",
  "is_active",
  "accessible_by_user_ids",
  "trigger_enrichment",
]);

export function validateContactField(fieldName: string, value: unknown): FieldValidation {
  if (READ_ONLY_FIELDS.has(fieldName)) {
    return {
      isValid: false,
      errors: [`Field "${fieldName}" is read-only and cannot be updated`],
    };
  }

  if (!ALLOWED_CONTACT_FIELDS.has(fieldName)) {
    return {
      isValid: false,
      errors: [`Field "${fieldName}" is not a valid contact field`],
    };
  }

  switch (fieldName) {
    case "email":
      return validateEmail(value);
    case "phone":
      return validatePhone(value);
    case "status":
      return validateStatus(value);
    case "tags":
      return validateTags(value);
    case "custom_fields":
      return validateCustomFields(value);
    case "email_aliases":
      return validateEmailAliases(value);
    case "phone_aliases":
      return validatePhoneAliases(value);
    case "social_links":
      return validateSocialLinks(value);
    case "is_active":
      return validateBoolean(value, "is_active");
    case "trigger_enrichment":
      return validateBoolean(value, "trigger_enrichment");
    case "accessible_by_user_ids":
      return validateStringArray(value, "accessible_by_user_ids");
    case "last_contacted_at":
    case "last_enriched_at":
    case "last_follow_up_task_created_at":
      return validateDateString(value, fieldName);
    case "name":
    case "first_name":
    case "last_name":
    case "company_name":
    case "address":
    case "city":
    case "state":
    case "country":
    case "zip_code":
    case "title":
    case "source":
    case "lifecycle_stage":
    case "notes":
      return validateString(value, fieldName);
    case "company_id":
      return validateUUID(value, "company_id");
    default:
      return { isValid: true, errors: [], sanitizedValue: value };
  }
}

export function validateContactUpdatePayload(
  updates: Record<string, unknown>
): { isValid: boolean; errors: string[]; sanitized: Record<string, unknown> } {
  const errors: string[] = [];
  const sanitized: Record<string, unknown> = {};

  for (const [fieldName, value] of Object.entries(updates)) {
    // `undefined` means "the caller did not supply this field"; `null` means
    // "clear it". Every field validator normalizes BOTH to `null`, so passing an
    // absent form field straight through would write NULL over a value the user
    // never touched — an edit form that omits `title` would blank the contact's
    // title. Drop absent keys before they reach the update payload.
    if (value === undefined) continue;

    const validation = validateContactField(fieldName, value);
    if (!validation.isValid) {
      errors.push(...validation.errors);
    } else {
      sanitized[fieldName] = validation.sanitizedValue;
    }
  }

  return { isValid: errors.length === 0, errors, sanitized };
}
